import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { translations } from "../data/translations";
import { PRODUCTS, TVA } from "../data/mock";
import { api, clearClientToken } from "../services/api";

const AppContext = createContext(null);

export const useApp = () => useContext(AppContext);

const normalizeCustom = (d) => ({
  id: d.id,
  name: { fr: d.name_fr, ar: d.name_fr },
  unit: { fr: d.unit_fr || "unité", ar: d.unit_fr || "وحدة" },
  desc: { fr: `Proposé par ${d.supplierName}.`, ar: `مقدَّم من ${d.supplierName}.` },
  vendor: d.supplierName,
  brand: d.brand || "",
  vendorType: { fr: "Fournisseur", ar: "مورّد" },
  category: d.category,
  priceHT: d.priceHT,
  moq: d.moq || 1,
  image: d.image || "https://images.unsplash.com/photo-1553546895-531931aa1aa8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  dlc: d.dlc || "",
  promo: d.promo || "",
  freeEvery: d.freeEvery || 0,
  custom: true
});

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("ssuq_lang") || "fr");
  const [customProducts, setCustomProducts] = useState([]);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ssuq_cart")) || []; } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [client, setClient] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("ssuq_client")) || null; } catch { return null; }
  });
  const isPro = !!client || !!sessionStorage.getItem("ssuq_admin");
  const logoutClient = () => {
    sessionStorage.removeItem("ssuq_client");
    clearClientToken();
    setClient(null);
    window.location.assign("/");
  };

  const t = useCallback((key) => translations[lang][key], [lang]);
  const tf = useCallback((obj) => (obj ? obj[lang] : ""), [lang]);

  const catalog = [...customProducts.map(normalizeCustom)];

  useEffect(() => {
    api.getCustomProducts().then(setCustomProducts).catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem("ssuq_lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = translations[lang].dir;
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("ssuq_cart", JSON.stringify(cart));
  }, [cart]);

  const toggleLang = () => setLang((l) => (l === "fr" ? "ar" : "fr"));

  const addToCart = (productId, qty) => {
    const product = catalog.find((p) => p.id === productId);
    if (!product) return;
    const quantity = Math.max(qty || product.moq, product.moq);
    setCart((prev) => {
      const existing = prev.find((i) => i.id === productId);
      if (existing) {
        return prev.map((i) => (i.id === productId ? { ...i, qty: i.qty + quantity } : i));
      }
      return [...prev, { id: productId, qty: quantity }];
    });
  };

  const updateQty = (productId, qty) => {
    const product = catalog.find((p) => p.id === productId);
    const min = product ? product.moq : 1;
    setCart((prev) => prev.map((i) => (i.id === productId ? { ...i, qty: Math.max(qty, min) } : i)));
  };

  const removeFromCart = (productId) =>
    setCart((prev) => prev.filter((i) => i.id !== productId));

  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((sum, i) => sum + i.qty, 0);

  const cartDetailed = cart.map((i) => {
    const p = catalog.find((x) => x.id === i.id);
    const freeEvery = p && p.freeEvery ? p.freeEvery : 0;
    const freeQty = freeEvery > 0 ? Math.floor(i.qty / freeEvery) : 0;
    const payable = i.qty - freeQty;
    return { ...p, qty: i.qty, freeQty, lineHT: p ? p.priceHT * payable : 0 };
  });

  const totalHT = cartDetailed.reduce((s, i) => s + i.lineHT, 0);
  const totalTVA = totalHT * TVA;
  const totalTTC = totalHT + totalTVA;

  return (
    <AppContext.Provider
      value={{
        lang, setLang, toggleLang, t, tf,
        catalog,
        cart, cartDetailed, cartCount, addToCart, updateQty, removeFromCart, clearCart,
        totalHT, totalTVA, totalTTC,
        cartOpen, setCartOpen,
        client, isPro, logoutClient
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
